import React, { useEffect, useMemo, useState } from 'react'
import {
    Box, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Checkbox, Typography, Button, TablePagination,
    Grid,
    Autocomplete,
    TextField,
    Chip
} from '@mui/material'
import { Colors } from '../../../Styles/Colors'
import RestAPIService from '../../../Utility/Services/RestAPIService'
import { Serviceurls } from '../../../Utility/API/Serviceurls'
import ToastMsg from '../../../Components/ToastMsg/ToastMsg'
import Loader from '../../../Components/Loader';
import { TableVirtuoso } from 'react-virtuoso'


export default function DrugMigration() {
    const [migrationData, setMigrationData] = useState([])
    const [selectedMainStores, setSelectedMainStores] = useState({})
    const [selectedSubDrugs, setSelectedSubDrugs] = useState({})
    const [filters, setFilters] = useState({
        brandName: '',
        genericName: '',
        dosage: null,
        strength: '',
        DosageTypes: [],
    })
    const [isLoader, setIsloader] = useState(false)
    const [errorMsg, seterrorMsg] = useState(false)
    const [errorMsgText, seterrorMsgText] = useState('')
    const [successMsg, setsuccessMsg] = useState(false)
    const [successMsgText, setsuccessMsgText] = useState('')
    const [mainPharmacyKey, setmainPharmacyKey] = useState('')
    const [subStoreKeys, setsubStoreKeys] = useState([])

    useEffect(() => {
        getDosageTypes();
        getDrugMigrationData();
    }, [])

    const filteredMigrationData = useMemo(() => {
        if (!migrationData?.data?.length) return []

        const brand = filters.brandName?.trim().toLowerCase()
        const generic = filters.genericName?.trim().toLowerCase()
        const dosage = filters.dosage?.toLowerCase?.() || ''
        const strength = filters.strength?.trim().toLowerCase()

        // NO FILTER APPLIED → SHOW ALL DATA
        const isAnyFilterApplied =
            brand || generic || dosage || strength

        if (!isAnyFilterApplied) {
            return migrationData.data
        }

        // 🔹 FILTER ONLY WHEN USER TYPES / SELECTS
        return migrationData.data.filter(row => {
            const mainDrug = row?.[mainPharmacyKey]
            if (!mainDrug) return false

            const matchesBrand =
                !brand || mainDrug.drug_name?.toLowerCase().includes(brand)

            const matchesGeneric =
                !generic || mainDrug.generic?.toLowerCase().includes(generic)

            const matchesDosage =
                !dosage || mainDrug.dosage?.toLowerCase().includes(dosage)

            const matchesStrength =
                !strength || mainDrug.strength?.toLowerCase().includes(strength)

            return (
                matchesBrand &&
                matchesGeneric &&
                matchesDosage &&
                matchesStrength
            )
        })
    }, [migrationData, filters, mainPharmacyKey])

    const flatRows = useMemo(() => {
        const rows = []

        filteredMigrationData?.forEach((row, idx) => {
            const maxRows = Math.max(
                1,
                ...subStoreKeys.map(store => row?.[store]?.length || 0)
            )

            for (let i = 0; i < maxRows; i++) {
                rows.push({
                    rowIndex: idx,
                    subIndex: i,
                    rowData: row
                })
            }
        })

        return rows
    }, [filteredMigrationData, subStoreKeys])


    const errorMessage = (msg) => {
        seterrorMsg(true)
        seterrorMsgText(msg)
    }

    const successMessage = (msg) => {
        setsuccessMsg(true)
        setsuccessMsgText(msg)
    }

    const getDrugMigrationData = () => {
        try {
            setIsloader(true);
            RestAPIService.getAll(Serviceurls.PH_DRUG_MIGRATIONS)
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        setIsloader(false);
                        setSelectedMainStores({})
                        setSelectedSubDrugs({})
                        const data = response?.data
                        setMigrationData(data || [])
                        setmainPharmacyKey(data?.all_pharmacy_names[0] || '')
                        setsubStoreKeys(data?.all_pharmacy_names.slice(1) || [])
                    }
                })
                .catch((e) => {
                    setIsloader(false);
                    errorMessage(e?.response?.data?.message || e?.message)
                })
        } catch (e) {
            setIsloader(false);
            errorMessage(e?.message)
        }
    };

    const getDosageTypes = () => {
        try {
            RestAPIService.getAll(Serviceurls.PHARMA_DOSAGE_TYPE_LIST)
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        setFilters(prev => ({
                            ...prev,
                            DosageTypes: response?.data?.data || []
                        }))
                    }
                })
                .catch((e) => {
                    errorMessage(e?.response?.data?.message || e?.message)
                })
        } catch (e) {
            errorMessage(e?.message)
        }
    };

    const payload = useMemo(() => {
        if (!filteredMigrationData?.length || !mainPharmacyKey) return []

        return Object.keys(selectedSubDrugs)
            .map(index => {
                // main must be selected
                if (!selectedMainStores[index]) return null

                const row = filteredMigrationData[index]
                const mainStore = row?.[mainPharmacyKey]
                if (!mainStore) return null

                const sub_pharmacy_drug_details = []

                subStoreKeys.forEach(store => {
                    row[store]?.forEach(drug => {
                        if (selectedSubDrugs[index]?.includes(drug.drug_id)) {
                            sub_pharmacy_drug_details.push({
                                drug_id: drug.drug_id,
                                pharmacy_id: drug?.pharmacy_id
                            })
                        }
                    })
                })

                // If no substore selected, don't send
                if (sub_pharmacy_drug_details.length === 0) return null

                return {
                    main_pharmacy_id: mainStore?.pharmacy_id,
                    main_pharmacy_drug_id: mainStore.drug_id,
                    sub_pharmacy_drug_details
                }
            })
            .filter(Boolean)
    }, [
        selectedSubDrugs,
        selectedMainStores,
        filteredMigrationData,
        mainPharmacyKey,
        subStoreKeys
    ])

    const postDrugMigrationData = () => {
        try {
            setIsloader(true);
            let data = payload
            RestAPIService.create(data, Serviceurls.PH_DRUG_MIGRATIONS)
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        setIsloader(false);
                        getDrugMigrationData()
                    }
                })
                .catch((e) => {
                    setIsloader(false);
                    errorMessage(e?.response?.data?.message || e?.message)
                })
        } catch (e) {
            setIsloader(false);
            errorMessage(e?.message)
        }
    };


    const isMergeDisabled = useMemo(() => {
        return payload.length === 0
    }, [payload])
    const msgClose = () => {
        seterrorMsg(false);
        seterrorMsgText('');
        setsuccessMsg(false);
        setsuccessMsgText('');
    }

    return (
        <Box sx={{ backgroundColor: Colors.configHomeBg, p: 1, height: '79.8vh' }}>
            <Box sx={{ p: 2, backgroundColor: '#fff', border: '1px solid #BFBFBF', borderRadius: '1px', height: '12vh' }}>
                <Grid container spacing={2}>
                    <Grid item xs={2.5}>
                        <TextField
                            sx={{ width: '18vw' }}
                            value={filters?.brandName}
                            onChange={(e) => {
                                setFilters(prev => ({
                                    ...prev,
                                    brandName: e.target.value,
                                }))
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder='Search Brand Name'
                        />
                    </Grid>

                    <Grid item xs={2.5}>
                        <TextField
                            sx={{ width: '18vw' }}
                            value={filters?.genericName}
                            onChange={(e) => {
                                setFilters(prev => ({
                                    ...prev,
                                    genericName: e.target.value,
                                }))
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder='Search Generic Name'
                        />
                    </Grid>

                    <Grid item xs={2.5}>
                        <Autocomplete
                            size="small"
                            options={filters?.DosageTypes}
                            value={filters.dosage}
                            getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                            onChange={(_, value) =>
                                setFilters(prev => ({
                                    ...prev,
                                    dosage: value,
                                }))
                            }
                            renderInput={(params) => (
                                <TextField sx={{ width: '18vw' }} {...params} label="Dosage Type" />
                            )}
                        />
                    </Grid>

                    <Grid item xs={2.5}>
                        <TextField
                            sx={{ width: '18vw' }}
                            value={filters?.strength}
                            onChange={(e) => {
                                setFilters(prev => ({
                                    ...prev,
                                    strength: e.target.value,
                                }))
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder='Search Strength'
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <Button variant='outlined' sx={{ textTransform: 'capitalize' }}
                            onClick={() => {
                                setFilters(prev => ({
                                    ...prev,
                                    brandName: '',
                                    genericName: '',
                                    dosage: null,
                                    strength: ''
                                }))
                            }}>Clear</Button>
                    </Grid>
                </Grid>
            </Box>
            <Paper sx={{ backgroundColor: 'white', height: '64vh', marginTop: '.5vw' }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" p={1}>
                    <Box display="flex" alignItems="center">
                        <Checkbox
                            size="small"
                            disabled={!filteredMigrationData?.length}
                            checked={
                                filteredMigrationData?.length > 0 &&
                                filteredMigrationData?.every((_, idx) => selectedMainStores[idx])
                            }
                            indeterminate={
                                Object.keys(selectedMainStores).length > 0 &&
                                !filteredMigrationData?.every((_, idx) => selectedMainStores[idx])
                            }
                            onChange={() => {
                                const allSelected =
                                    filteredMigrationData?.length > 0 &&
                                    filteredMigrationData?.every((_, idx) => selectedMainStores[idx])

                                if (allSelected) {
                                    setSelectedMainStores({})
                                    setSelectedSubDrugs({})
                                } else {
                                    const all = {}
                                    filteredMigrationData?.forEach((_, idx) => (all[idx] = true))
                                    setSelectedMainStores(all)
                                }
                            }}
                        />
                        <Typography fontWeight={600}>Select All</Typography>
                    </Box>
                    {/* isMergeDisabled */}
                    <Button variant="contained" sx={{ textTransform: 'capitalize' }} disabled={isMergeDisabled} onClick={() => postDrugMigrationData()}>Merge Drug Name</Button>
                </Box>

                {/* Table */}
                <TableContainer sx={{ height: '56.7vh' }}>
                    <TableVirtuoso
                        style={{ height: '56.7vh' }}
                        data={flatRows}
                        components={{
                            Table: (props) => <Table {...props} size="small"
                                sx={{
                                    tableLayout: 'fixed',
                                    width: '100%',
                                }} />,
                            TableRow,
                            TableHead,
                            TableBody,
                        }}
                        fixedHeaderContent={() => (
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#FAF9F9', whiteSpace: 'nowrap' }}>
                                    {mainPharmacyKey}
                                </TableCell>
                                {subStoreKeys.map(name => (
                                    <TableCell
                                        key={name}
                                        sx={{ fontWeight: 'bold', backgroundColor: '#FAF9F9', whiteSpace: 'nowrap' }}
                                    >
                                        {name}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )}
                        itemContent={(index, item) => {
                            const { rowIndex, subIndex, rowData } = item
                            const mainStore = rowData?.[mainPharmacyKey]

                            return (
                                <>
                                    <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                                        {subIndex === 0 && (
                                            <Box>
                                                <Box display="flex" gap={1}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={!!selectedMainStores[rowIndex]}
                                                        onChange={() => {
                                                            setSelectedMainStores(prev => {
                                                                const updated = {
                                                                    ...prev,
                                                                    [rowIndex]: !prev[rowIndex]
                                                                }

                                                                if (!updated[rowIndex]) {
                                                                    setSelectedSubDrugs(sub => {
                                                                        const copy = { ...sub }
                                                                        delete copy[rowIndex]
                                                                        return copy
                                                                    })
                                                                }

                                                                return updated
                                                            })
                                                        }}
                                                    />
                                                    <Box>
                                                        <Typography fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {mainStore?.drug_name}
                                                            {mainStore?.is_main_pharmacy_drug_migrated && (
                                                                <Chip
                                                                    sx={{ backgroundColor: '#04B7B1', color: 'white', height: '1.5vw' }}
                                                                    label="Migrated"
                                                                    variant="contained"
                                                                    size="small"
                                                                />
                                                            )}
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            Generic Name : {mainStore?.generic}
                                                        </Typography><br />
                                                        <Typography variant="caption">
                                                            Dosage Type : {mainStore?.dosage}
                                                        </Typography><br />
                                                        <Typography variant="caption">
                                                            Dosage Strength : {mainStore?.strength}
                                                        </Typography><br />
                                                        <Typography variant="caption">
                                                            HSN Code : {mainStore?.hsn_code}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box></Box>
                                            </Box>
                                        )}
                                    </TableCell>

                                    {subStoreKeys.map(pharmacy => {
                                        const drug = rowData?.[pharmacy]?.[subIndex]

                                        return (
                                            <TableCell key={pharmacy}>
                                                {drug && (
                                                    <Box display="flex" gap={1}>
                                                        <Checkbox
                                                            size="small"
                                                            disabled={!selectedMainStores[rowIndex]}
                                                            checked={
                                                                selectedSubDrugs[rowIndex]?.includes(drug.drug_id) || false
                                                            }
                                                            onChange={() => {
                                                                setSelectedSubDrugs(prev => {
                                                                    const current = prev[rowIndex] || []

                                                                    const updated = current.includes(drug.drug_id)
                                                                        ? current.filter(id => id !== drug.drug_id)
                                                                        : [...current, drug.drug_id]

                                                                    setSelectedMainStores(ms => ({
                                                                        ...ms,
                                                                        [rowIndex]: true
                                                                    }))

                                                                    return {
                                                                        ...prev,
                                                                        [rowIndex]: updated
                                                                    }
                                                                })
                                                            }}
                                                        />
                                                        <Box>
                                                            <Typography fontWeight={600}>
                                                                {drug.drug_name}
                                                            </Typography>
                                                            <Typography variant="caption">
                                                                Generic Name : {drug.generic}
                                                            </Typography><br />
                                                            <Typography variant="caption">
                                                                Dosage Type : {drug.dosage}
                                                            </Typography><br />
                                                            <Typography variant="caption">
                                                                Dosage Strength : {drug.strength}
                                                            </Typography><br />
                                                            <Typography variant="caption">
                                                                HSN Code : {drug.hsn_code}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        )
                                    })}
                                </>
                            )
                        }}
                    />
                </TableContainer>
            </Paper >
            {
                (errorMsg && errorMsgText) ?
                    <ToastMsg
                        severity={'error'}
                        msg={errorMsgText}
                        msgPop={msgClose.bind(this)}
                    />
                    : null
            }
            {
                (successMsg && successMsgText) ?
                    <ToastMsg
                        severity={'success'}
                        msg={successMsgText}
                        msgPop={msgClose.bind(this)}
                    />
                    : null
            }
            <Loader loaderOpen={isLoader} />
        </Box >
    )
}
